import React from 'react';
import { useParams } from 'wouter';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  Server, 
  Database, 
  Network, 
  Shield, 
  Zap, 
  BarChart3,
  Cloud,
  ArrowLeft
} from 'lucide-react';
import { Link } from 'wouter';

const CostcoArchitecturePage: React.FC = () => {
  const { clientCode } = useParams<{ clientCode: string }>();

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-4">
            <Link href="/">
              <button className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors">
                <ArrowLeft className="w-4 h-4" />
                <span className="text-sm">Back to Dashboard</span>
              </button>
            </Link>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="p-3 bg-shiftleft-100 dark:bg-shiftleft-900 rounded-lg text-shiftleft-600 dark:text-shiftleft-400">
              <Server className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                Costco HighJump Architecture Configuration
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-1">
                Current architecture analysis and configuration details for {clientCode}
              </p>
            </div>
          </div>
        </div>

        {/* Architecture Overview */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <div className="space-y-4">
            <div className="bg-shiftleft-50 dark:bg-shiftleft-900/20 rounded-lg p-4">
              <h3 className="font-semibold text-shiftleft-900 dark:text-shiftleft-100 mb-2">🏗️ Architecture Overview</h3>
              <ul className="text-sm text-shiftleft-800 dark:text-shiftleft-200 space-y-1">
                <li>• Multi-Region Azure Deployment</li>
                <li>• Production: Azure West US 2</li>
                <li>• DR: Azure Central US</li>
                <li>• Enterprise-Scale Design</li>
                <li>• Cloud-Native Infrastructure</li>
              </ul>
            </div>

            <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
              <h3 className="font-semibold text-green-900 dark:text-green-100 mb-2">🔄 High Availability</h3>
              <ul className="text-sm text-green-800 dark:text-green-200 space-y-1">
                <li>• Always On Availability Groups</li>
                <li>• Synchronous replication (local)</li>
                <li>• Asynchronous replication (DR)</li>
                <li>• Load-balanced application tier</li>
                <li>• Automated failover procedures</li>
              </ul>
            </div>
          </div>

          <div className="space-y-4">
            <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4">
              <h3 className="font-semibold text-purple-900 dark:text-purple-100 mb-2">🔗 Integration Architecture</h3>
              <ul className="text-sm text-purple-800 dark:text-purple-200 space-y-1">
                <li>• Dedicated integration services layer</li>
                <li>• Multiple ERP system connections</li>
                <li>• Real-time and batch processing</li>
                <li>• Message queuing for reliability</li>
                <li>• API gateway for external systems</li>
              </ul>
            </div>

            <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-4">
              <h3 className="font-semibold text-orange-900 dark:text-orange-100 mb-2">📊 Key Strengths</h3>
              <ul className="text-sm text-orange-800 dark:text-orange-200 space-y-1">
                <li>• Scalable multi-tier architecture</li>
                <li>• Redundancy at every layer</li>
                <li>• Optimized for performance</li>
                <li>• Comprehensive DR strategy</li>
                <li>• Flexible integration patterns</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Regional Configuration */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Network className="w-5 h-5" />
              Regional Configuration
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <h4 className="font-medium text-gray-900 dark:text-white">Primary Region (Azure West US 2)</h4>
                </div>
                <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                  <li>• Production HighJump application servers</li>
                  <li>• SQL Server Always On primary replicas</li>
                  <li>• Integration services layer</li>
                  <li>• Load balancers for distribution</li>
                  <li>• Premium storage for databases</li>
                </ul>
              </div>

              <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-3 h-3 bg-shiftleft-500 rounded-full"></div>
                  <h4 className="font-medium text-gray-900 dark:text-white">DR Region (Azure Central US)</h4>
                </div>
                <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                  <li>• Secondary SQL Server replicas</li>
                  <li>• Standby application infrastructure</li>
                  <li>• Cross-region replication</li>
                  <li>• Automated failover procedures</li>
                  <li>• Business continuity assurance</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Recommended Focus Areas */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>🎯 Recommended Focus Areas</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Zap className="w-4 h-4 text-yellow-600" />
                  <h4 className="font-medium text-yellow-900 dark:text-yellow-100">Performance Optimization</h4>
                </div>
                <ul className="text-xs text-yellow-800 dark:text-yellow-200 space-y-1">
                  <li>• Monitor Always On performance</li>
                  <li>• Optimize replication latency</li>
                  <li>• Tune load balancer algorithms</li>
                </ul>
              </div>

              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Shield className="w-4 h-4 text-red-600" />
                  <h4 className="font-medium text-red-900 dark:text-red-100">Security Hardening</h4>
                </div>
                <ul className="text-xs text-red-800 dark:text-red-200 space-y-1">
                  <li>• Network segmentation</li>
                  <li>• Encryption in transit/rest</li>
                  <li>• Access control policies</li>
                </ul>
              </div>

              <div className="bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <BarChart3 className="w-4 h-4 text-indigo-600" />
                  <h4 className="font-medium text-indigo-900 dark:text-indigo-100">Monitoring & Alerting</h4>
                </div>
                <ul className="text-xs text-indigo-800 dark:text-indigo-200 space-y-1">
                  <li>• End-to-end performance monitoring</li>
                  <li>• Database performance metrics</li>
                  <li>• DR readiness validation</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Data Flow Patterns */}
        <Card>
          <CardHeader>
            <CardTitle>🔄 Data Flow Patterns</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div className="text-center">
                <div className="bg-shiftleft-100 dark:bg-shiftleft-900/30 rounded-lg p-3 mb-2">
                  <Database className="w-6 h-6 text-shiftleft-600 mx-auto mb-1" />
                  <div className="font-medium text-shiftleft-900 dark:text-shiftleft-100">Inbound</div>
                </div>
                <div className="text-gray-600 dark:text-gray-400">
                  ERP → Integration Layer → HighJump
                </div>
              </div>
              <div className="text-center">
                <div className="bg-green-100 dark:bg-green-900/30 rounded-lg p-3 mb-2">
                  <Network className="w-6 h-6 text-green-600 mx-auto mb-1" />
                  <div className="font-medium text-green-900 dark:text-green-100">Outbound</div>
                </div>
                <div className="text-gray-600 dark:text-gray-400">
                  HighJump → Integration Layer → External Systems
                </div>
              </div>
              <div className="text-center">
                <div className="bg-purple-100 dark:bg-purple-900/30 rounded-lg p-3 mb-2">
                  <Cloud className="w-6 h-6 text-purple-600 mx-auto mb-1" />
                  <div className="font-medium text-purple-900 dark:text-purple-100">Cross-Region</div>
                </div>
                <div className="text-gray-600 dark:text-gray-400">
                  Production → DR (continuous replication)
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CostcoArchitecturePage;